<script setup lang="ts">
import { ref } from "vue";
import { useGroupStore } from "@/stores/groups.ts";
import TeamDisplay from "@/components/TeamDisplay.vue";
import { useTeamStore } from "@/stores/teams.ts";

const groupStore = useGroupStore();
const teamStore = useTeamStore();

const newGroupName = ref("");

function newGroup() {
  if (!newGroupName.value.trim()) return;
  groupStore.put({
    name: newGroupName.value,
    placements: [],
    fixed: false,
  });
  newGroupName.value = "";
}

function save(groupId: string) {
  const group = groupStore.groups.find(g => g.id === groupId);
  if (!group) return;
  groupStore.put(group);
}

function hasSameNumber(places: number[]) {
  const uniquePlaces = new Set(places);
  return uniquePlaces.size !== places.length;
}

</script>

<template>
  <div class="page">
    <div class="group-edit panel" v-for="group in groupStore.groups" :key="group.id">
      <h2>Gruppe {{ group.name }}</h2>
      <input
        v-model="group.name"
        @blur="save(group.id)"
        placeholder="Gruppe Name"
        class="l2"
      />
      <span>
        <input
          :checked="group.fixed"
          type="checkbox"
          @input="$event => {
            const target = $event.target as HTMLInputElement;
            group.fixed = target.checked;
            save(group.id);
          }"
          id="fixed"
        />
        <label for="fixed">Manuelle Platzierung</label>
      </span>
      <button @click="groupStore.remove(group)" class="l2">Gruppe löschen</button>
      <table>
        <thead>
          <tr>
            <th>Platzierung</th>
            <th>Team</th>
            <th>Punkte</th>
            <th>Spiele</th>
            <th>Tore</th>
            <th>Aktion</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(placement, index) in group.placements" :key="index">
            <td>
              <span v-if="!group.fixed">
                {{ placement.place }}
              </span>
              <input
                v-model.number="placement.place"
                class="l2"
                type="number"
                min="1"
                v-else
                @input="save(group.id)"
              />
            </td>
            <td>
              <TeamDisplay :team="teamStore.getTeamById(placement.team)" />
            </td>
            <td>
              {{ placement.points }}
            </td>
            <td>
              ({{ placement.wins }}/{{ placement.draws }}/{{ placement.losses }})
            </td>
            <td>
              {{ placement.goalsScored }} : {{ placement.goalsAgainst }}
            </td>
            <td>
              <button class="circle danger" @click="group.placements.splice(index, 1); save(group.id)">X</button>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="warning-box" v-if="hasSameNumber(group.placements.map(p => p.points))">
        Mehrere Teams haben die gleiche Punktzahl. Bitte überprüfen Sie die Platzierungen.
        Falls alle Gruppenspiele abgeschlossen sind, müssen die Platzierungen manuell angepasst werden.
      </div>
      <div class="add-team">
        <label for="team-select">Team hinzufügen:</label>
        <select id="team-select" class="input-like l2" @change="$event => {
          const target = $event.target as HTMLSelectElement;
          const teamId = target.value;
          if (teamId) {
            group.placements.push({
              team: teamId,
              place: 0,
              points: 0,
              wins: 0,
              draws: 0,
              losses: 0,
              goalsScored: 0,
              goalsAgainst: 0,
            });
            save(group.id);
          }
          target.value = '';
        }">
          <option value="" disabled>-- Team auswählen --</option>
          <option
            v-for="team in teamStore.teams.filter(t => !group.placements.some(p => p.team === t.id))"
            :key="team.id"
            :value="team.id"
          >
            {{ team.name }}
          </option>
        </select>
      </div>
      <div class="delete-group">
        <button @click="groupStore.remove(group)" class="l2 danger">Gruppe löschen</button>
      </div>
    </div>
    <div class="game-create panel">
      <h2>Neue Gruppe erstellen</h2>
      <form @submit.prevent="newGroup">
        <label>
          Name:
          <input v-model="newGroupName" class="l2" />
        </label>
        <button class="l2" type="submit">Gruppe Erstellen</button>
      </form>
    </div>
  </div>
</template>

<style scoped>

.group-edit td {
  text-align: center;
  vertical-align: middle;
}

.game-create form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>
